import { useState, useEffect } from "react";

import { Info, Link } from "lucide-react";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { SiGooglecalendar, SiCalendly, SiSquare } from "react-icons/si";
import { GoHighLevel } from "./components/GhlIcon";
import { AcuityScheduling } from "./components/AquityIcon";
import { 
  useActiveIntegration, 
  useConnectCalendar, 
  useDisconnectCalendar,
  useIntegrationLocalStorage,
  type CalendarProvider
} from "@/hooks/useIntegrations";
import { useCalendarConfigState } from "@/hooks/useCalendarConfig";
import { useOAuthCallback } from "@/hooks/useOAuthCallback";
import { IntegrationSceleton } from "./components/IntegrationSceleton";
import { IntegrationError } from "./components/IntegrationError";
import { ActiveIntegration } from "./components/ActiveIntegration";
import { IntegrationCard } from "./components/IntegrationCard";
import { BackendError } from "./components/BackendError";
import { InfoDialog } from "./components/InfoModal";
interface ProviderConfig {
  name: string;
  icon?: React.ReactNode;
  checkKey: string;
}

// --- Added GHL, Outlook and custom icons to the configuration object ---
const PROVIDER_CONFIGS: Record<CalendarProvider, ProviderConfig> = {
  google: {
    name: "Google Calendar",
    checkKey: "google",
    icon: <SiGooglecalendar className="size-5 mr-2" />,
  },
  acuity: {
    name: "Acuity Scheduling",
    checkKey: "acuity",
    icon: <AcuityScheduling className="size-5 mr-2" />,
  },
  calendly: {
    name: "Calendly",
    checkKey: "calendly",
    icon: <SiCalendly className="size-5 mr-2" />,
  },
  square: {
    name: "Square Appointments",
    checkKey: "square",
    icon: <SiSquare className="size-5 mr-2" />,
  },
  ghl: {
    name: "GoHighLevel",
    checkKey: "ghl",
    icon: <GoHighLevel className="size-5 mr-2" />,
  },
  outlook: {
    name: "Outlook Calendar",
    checkKey: "outlook",
    icon: <PiMicrosoftOutlookLogoFill className="size-5 mr-2" />,
  },
};

export default function CalendarIntegrationsTab() {
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);
  const [connectingProvider, setConnectingProvider] = useState<CalendarProvider | null>(null);
  
  // Hooks for data fetching and mutations
  const { data: activeIntegration, isLoading: isLoadingIntegrations, error: integrationsError } = useActiveIntegration();
  const connectCalendarMutation = useConnectCalendar();
  const disconnectCalendarMutation = useDisconnectCalendar();
  const { hasIntegrationInStorage } = useIntegrationLocalStorage();
  
  // Configuration and OAuth hooks
  const { configuredProviders, backendConfigError, isCheckingBackendConfig } = useCalendarConfigState();
  useOAuthCallback(); // Handle OAuth callback URL cleanup

  const handleConnectCalendar = (provider: CalendarProvider) => {
    if (connectCalendarMutation.isPending) return;
    
    setConnectingProvider(provider);
    connectCalendarMutation.mutate(provider);
  };

  useEffect(() => {
    if (!connectCalendarMutation.isPending) setConnectingProvider(null);
  }, [connectCalendarMutation.isPending]);

  const getProviderDisplayName = (providerKey: string): string => {
    return (
      PROVIDER_CONFIGS[providerKey as CalendarProvider]?.name ||
      providerKey.charAt(0).toUpperCase() + providerKey.slice(1)
    );
  };

  if (
    !isCheckingBackendConfig &&
    !isLoadingIntegrations &&
    backendConfigError &&
    configuredProviders.length === 0
  ) {
    return (
      <BackendError backendConfigError={backendConfigError} />
    );
  }

  return (
    <div className="space-y-8 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Calendar Connections
          </h1>
          <p className="text-gray-600 mt-1">
            Connect a calendar to allow the AI Employee to manage your
            appointments. Only one calendar can be active at a time.
          </p>
        </div>
        <InfoDialog />
      </div>

      {!isCheckingBackendConfig &&
        !isLoadingIntegrations &&
        backendConfigError &&
        configuredProviders.length > 0 && (
          <div
            className="p-4 mb-4 text-sm text-yellow-800 bg-yellow-50 rounded-lg border border-yellow-200"
            role="alert"
          >
            <div className="flex items-center">
              <Info className="inline w-5 h-5 mr-2 text-yellow-600" />
              <span className="font-semibold">Configuration Notice:</span>
            </div>
            <p className="ml-7">{backendConfigError}</p>
          </div>
        )}

      {isLoadingIntegrations && hasIntegrationInStorage ? (
        // Show integration skeleton if localStorage says integration exists
        <IntegrationSceleton />
      ) : integrationsError ? (
        <IntegrationError error={integrationsError} />
      ) : activeIntegration ? (
        <ActiveIntegration 
          activeIntegration={activeIntegration} 
          selectedIntegrationId={selectedIntegrationId} 
          setSelectedIntegrationId={setSelectedIntegrationId} 
          getProviderDisplayName={getProviderDisplayName} 
          disconnectCalendarMutation={disconnectCalendarMutation} 
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(Object.keys(PROVIDER_CONFIGS) as CalendarProvider[]).map(item => (
              <IntegrationCard
                key={item}
                providerKey={item}
                configuredProviders={configuredProviders}
                connectingProvider={connectingProvider}
                isCheckingBackendConfig={isCheckingBackendConfig}
                connectCalendarMutation={connectCalendarMutation}
                handleConnectCalendar={handleConnectCalendar}
                PROVIDER_CONFIGS={PROVIDER_CONFIGS}
              />
            ))}
          </div>
          <div className="text-center py-12 border-dashed border-2 border-gray-300 rounded-lg text-gray-500 mt-8 bg-gray-50">
            <Link className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="font-medium">No calendar connected yet.</p>
            <p className="text-sm">
              Select an option above to get started.
            </p>
          </div>
        </>
      )}
    </div>
  );
}